/**
 * Application Routes
 *
 * Centralized, type-safe route definitions with proper enum support.
 * Use RouteKey enum for type-safe route references.
 *
 * @module shared/constants/routes
 */

// ============================================
// Route Key Enum
// ============================================

/**
 * Enum of all route keys in the application.
 * Use this instead of string literals for type safety.
 */
export enum RouteKey {
  // Root
  HOME = 'HOME',

  // Authentication (Public)
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  RESET_PASSWORD = 'RESET_PASSWORD',
  AUTH_CALLBACK = 'AUTH_CALLBACK',
  ACCEPT_INVITE = 'ACCEPT_INVITE',

  // Main App (Protected)
  DASHBOARD = 'DASHBOARD',
  DEMO = 'DEMO',
  CHANGE_PASSWORD = 'CHANGE_PASSWORD',
  SETTINGS = 'SETTINGS',

  // Family Management
  ONBOARDING = 'ONBOARDING',
  FAMILY_SETTINGS = 'FAMILY_SETTINGS',

  // Family Features
  FAMILY_CALENDAR = 'FAMILY_CALENDAR',
  ALL_EVENTS = 'ALL_EVENTS',
  MEMBER_PROFILE = 'MEMBER_PROFILE',
  TODO_LIST = 'TODO_LIST',
  ALBUMS = 'ALBUMS',
  NOTIFICATIONS = 'NOTIFICATIONS',

  // Public sharing
  SHARED_CALENDAR = 'SHARED_CALENDAR',

  // Hidden Routes
  LOVE = 'LOVE',

  // P2P Encrypted Chat
  P2P_CHAT = 'P2P_CHAT',

  // Monitoring (Hidden)
  MONITORING = 'MONITORING',
  MONITORING_DASHBOARD = 'MONITORING_DASHBOARD',
  MONITORING_CONFIG = 'MONITORING_CONFIG',
  MONITORING_LOGS = 'MONITORING_LOGS',

  // System
  //
  // There is deliberately no OFFLINE key here. `/offline` used to be
  // registered — route, path and PUBLIC_ROUTES entry — but nothing in the app
  // ever navigated to it and Workbox never served it either: `navigateFallback`
  // in vite.config.ts is `/index.html`, so the cached SPA shell answers every
  // offline navigation. The affordance the user actually sees is
  // `OfflineIndicator` in the header. Do not re-add the key without also
  // giving it a caller.
  NOT_FOUND = 'NOT_FOUND',
}

// ============================================
// Route Paths Mapping
// ============================================

/**
 * Mapping from RouteKey to actual path strings
 */
export const ROUTE_PATHS: Readonly<Record<RouteKey, string>> = {
  [RouteKey.HOME]: '/',
  [RouteKey.LOGIN]: '/login',
  [RouteKey.REGISTER]: '/register',
  [RouteKey.FORGOT_PASSWORD]: '/forgot-password',
  [RouteKey.RESET_PASSWORD]: '/reset-password',
  [RouteKey.AUTH_CALLBACK]: '/auth/callback',
  [RouteKey.ACCEPT_INVITE]: '/accept-invite',
  [RouteKey.DASHBOARD]: '/dashboard',
  [RouteKey.DEMO]: '/demo',
  [RouteKey.CHANGE_PASSWORD]: '/change-password',
  [RouteKey.SETTINGS]: '/settings',
  [RouteKey.ONBOARDING]: '/onboarding',
  [RouteKey.FAMILY_SETTINGS]: '/family-settings',
  [RouteKey.FAMILY_CALENDAR]: '/family-calendar',
  [RouteKey.ALL_EVENTS]: '/family-calendar/events',
  [RouteKey.MEMBER_PROFILE]: '/family-calendar/member/:memberId',
  [RouteKey.TODO_LIST]: '/todo-list',
  [RouteKey.ALBUMS]: '/albums',
  [RouteKey.NOTIFICATIONS]: '/notifications',
  [RouteKey.SHARED_CALENDAR]: '/shared/:token',
  [RouteKey.LOVE]: '/madivladi',
  [RouteKey.P2P_CHAT]: '/chat',
  [RouteKey.MONITORING]: '/monitoring',
  [RouteKey.MONITORING_DASHBOARD]: '/monitoring/dashboard',
  [RouteKey.MONITORING_CONFIG]: '/monitoring/config',
  [RouteKey.MONITORING_LOGS]: '/monitoring/logs',
  [RouteKey.NOT_FOUND]: '*',
} as const;

// ============================================
// Route Parameters Types
// ============================================

/**
 * Parameters for routes that support URL search params.
 * The key is the property name, value is what appears in URL.
 */
export interface CalendarParams {
  readonly date?: string;
  readonly eventId?: string;
  readonly tab?: 'labels' | 'activity';
  readonly highlightEvent?: string;
}

export interface TodoListParams {
  readonly taskId?: string;
  readonly filter?: string;
}

export interface AlbumsParams {
  readonly albumId?: string;
  readonly photoId?: string;
}

export interface OnboardingParams {
  readonly code?: string;
  /** Family id carried by invite links — context for the join flow. */
  readonly family?: string;
}

export interface AcceptInviteParams {
  /** Family id from the invite email redirect. */
  readonly family?: string;
}

/**
 * Map of routes to their parameter types.
 * Routes not in this map don't have parameters.
 */
export interface RouteParamsMap {
  [RouteKey.FAMILY_CALENDAR]: CalendarParams;
  [RouteKey.TODO_LIST]: TodoListParams;
  [RouteKey.ALBUMS]: AlbumsParams;
  [RouteKey.ONBOARDING]: OnboardingParams;
  [RouteKey.ACCEPT_INVITE]: AcceptInviteParams;
}

/**
 * Routes that have parameters
 */
export type RoutesWithParams = keyof RouteParamsMap;

/**
 * Routes without parameters
 */
export type RoutesWithoutParams = Exclude<RouteKey, RoutesWithParams>;

// ============================================
// Search Param Keys Mapping
// ============================================

/**
 * Mapping from param property names to URL search param keys.
 * This allows internal property names to differ from URL keys.
 */
const PARAM_KEY_MAP: Readonly<Record<string, string>> = {
  // Calendar
  date: 'date',
  eventId: 'event',
  // Todo
  taskId: 'task',
  filter: 'filter',
  // Albums
  albumId: 'album',
  photoId: 'photo',
  // Onboarding / accept-invite
  code: 'code',
  family: 'family',
} as const;

/**
 * Reverse mapping from URL keys to property names
 */
const URL_KEY_TO_PARAM: Readonly<Record<string, string>> = {
  date: 'date',
  event: 'eventId',
  task: 'taskId',
  filter: 'filter',
  album: 'albumId',
  photo: 'photoId',
  code: 'code',
  family: 'family',
} as const;

// ============================================
// Route Target Type (for navigation)
// ============================================

/**
 * Navigation target for routes without parameters
 */
interface SimpleRouteTarget {
  readonly route: RoutesWithoutParams;
  readonly params?: undefined;
}

/**
 * Navigation target for calendar route
 */
interface CalendarRouteTarget {
  readonly route: RouteKey.FAMILY_CALENDAR;
  readonly params?: CalendarParams;
}

/**
 * Navigation target for todo list route
 */
interface TodoListRouteTarget {
  readonly route: RouteKey.TODO_LIST;
  readonly params?: TodoListParams;
}

/**
 * Navigation target for albums route
 */
interface AlbumsRouteTarget {
  readonly route: RouteKey.ALBUMS;
  readonly params?: AlbumsParams;
}

/**
 * Navigation target for the onboarding route (optional invite code)
 */
interface OnboardingRouteTarget {
  readonly route: RouteKey.ONBOARDING;
  readonly params?: OnboardingParams;
}

/**
 * Navigation target for the accept-invite route (optional family context)
 */
interface AcceptInviteRouteTarget {
  readonly route: RouteKey.ACCEPT_INVITE;
  readonly params?: AcceptInviteParams;
}

/**
 * A navigation target - either a simple route or a route with params.
 * This is the main type used for type-safe navigation.
 */
export type RouteTarget =
  | SimpleRouteTarget
  | CalendarRouteTarget
  | TodoListRouteTarget
  | AlbumsRouteTarget
  | OnboardingRouteTarget
  | AcceptInviteRouteTarget;

// ============================================
// URL Builder Functions
// ============================================

/**
 * Build a URL from a route target.
 * Handles both simple routes and routes with search params.
 *
 * @example
 * buildUrl({ route: RouteKey.DASHBOARD })
 * // => '/dashboard'
 *
 * @example
 * buildUrl({ route: RouteKey.FAMILY_CALENDAR, params: { date: '2025-12-25' } })
 * // => '/family-calendar?date=2025-12-25'
 */
export function buildUrl(target: RouteTarget): string {
  const basePath = ROUTE_PATHS[target.route];

  if (!target.params) {
    return basePath;
  }

  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(target.params)) {
    if (value !== undefined && value !== null && value !== '') {
      const paramKey = PARAM_KEY_MAP[key] ?? key;
      searchParams.set(paramKey, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

/**
 * Get the path for a route without params.
 * Use this for static route references.
 *
 * @example
 * getPath(RouteKey.DASHBOARD) // => '/dashboard'
 */
export function getPath(route: RouteKey): string {
  return ROUTE_PATHS[route];
}

// ============================================
// URL Params Parser
// ============================================

/**
 * Parse URL search params for a specific route.
 * Returns typed params object.
 *
 * @example
 * const params = parseRouteParams(RouteKey.FAMILY_CALENDAR, searchParams);
 * // params: { date?: string; eventId?: string }
 */
export function parseRouteParams<K extends RoutesWithParams>(
  _route: K,
  searchParams: URLSearchParams
): Partial<RouteParamsMap[K]> {
  const result: Record<string, string> = {};

  for (const [urlKey, value] of searchParams.entries()) {
    const paramKey = URL_KEY_TO_PARAM[urlKey] ?? urlKey;
    result[paramKey] = value;
  }

  return result as Partial<RouteParamsMap[K]>;
}

// ============================================
// Route Groups (for permissions/navigation)
// ============================================

/**
 * Public routes - no authentication required
 */
export const PUBLIC_ROUTES: readonly RouteKey[] = [
  RouteKey.LOGIN,
  RouteKey.REGISTER,
  RouteKey.FORGOT_PASSWORD,
  RouteKey.RESET_PASSWORD,
  RouteKey.AUTH_CALLBACK,
] as const;

/**
 * Protected routes - require authentication
 */
export const PROTECTED_ROUTES: readonly RouteKey[] = [
  RouteKey.DASHBOARD,
  RouteKey.DEMO,
  RouteKey.CHANGE_PASSWORD,
  RouteKey.SETTINGS,
  RouteKey.FAMILY_CALENDAR,
  RouteKey.TODO_LIST,
  RouteKey.ALBUMS,
  RouteKey.LOVE,
  RouteKey.P2P_CHAT,
  RouteKey.MONITORING,
  RouteKey.MONITORING_DASHBOARD,
  RouteKey.MONITORING_CONFIG,
  RouteKey.MONITORING_LOGS,
] as const;

/**
 * Main navigation routes - shown in sidebar
 */
export const MAIN_NAV_ROUTES = [
  { route: RouteKey.DASHBOARD, label: 'Начало', icon: 'Home' },
  { route: RouteKey.FAMILY_CALENDAR, label: 'Календар', icon: 'Calendar' },
  { route: RouteKey.TODO_LIST, label: 'Задачи', icon: 'CheckSquare' },
  { route: RouteKey.ALBUMS, label: 'Албуми', icon: 'Camera' },
  { route: RouteKey.P2P_CHAT, label: 'Чат', icon: 'MessageCircle' },
] as const;

// ============================================
// Backend-Written Notification Links
// ============================================

/**
 * One `notifications.link` literal that lives outside this module.
 */
export interface BackendNotificationLink {
  /** The route the link is meant to open. */
  readonly route: RouteKey;
  /** The exact string the writer stores in `public.notifications.link`. */
  readonly link: string;
  /** Where that literal is written, so a reviewer can go and change it. */
  readonly writtenBy: string;
}

/**
 * Every `link` value the BACKEND can put on a row in `public.notifications`.
 *
 * WHY THIS EXISTS
 * A notification's link is the one string in the app that is written by a
 * plpgsql trigger or a Deno edge function and then handed straight to the
 * router. Neither writer can import `ROUTE_PATHS` — a function body in
 * Postgres and a module under `supabase/functions/` have no way to reach
 * `src/` — so both have drifted, twice, in exactly the same way:
 *
 *   - `notify-event-reminder` wrote '/calendar'  (corrected in Wave 2)
 *   - `public.notify_appreciation` wrote '/love' (corrected in migration 022)
 *
 * Both shipped notifications that opened NotFound when tapped, and nothing in
 * the repo noticed either one. This list is the artefact a unit test can hold
 * those literals against; see `__tests__/notificationLinks.test.ts`.
 *
 * NOT LISTED: links the client builds itself. `usePresence` composes its chat
 * invite link from `getPath(RouteKey.P2P_CHAT)`, so it is correct by
 * construction and has nothing to drift from. Only hand-copied literals
 * belong here.
 *
 * HOW TO REGENERATE — the database half, read-only, against the live project:
 *
 * ```sh
 * ./node_modules/.bin/supabase db query --linked \
 *   "select p.oid::regprocedure::text as fn, p.prosrc
 *      from pg_proc p
 *      join pg_namespace n on n.oid = p.pronamespace
 *     where n.nspname = 'public'
 *       and (p.prosrc ilike '%into public.notifications%'
 *            or p.prosrc ilike '%into notifications%');"
 * ```
 *
 * Migration 022's post-condition block prints the same inventory into the
 * `db push` output, so a release also reports it. The application half is
 * `grep -rn "link:" supabase/functions`. This is a HAND-MAINTAINED mirror in
 * the same spirit as `src/shared/types/database.ts`: the test proves the
 * entries here are real routes and that nothing checked in writes a link
 * missing from the list, but only a human keeps it honest against a live
 * schema that has been hand-edited before.
 */
export const NOTIFICATION_LINKS_WRITTEN_BY_BACKEND: readonly BackendNotificationLink[] =
  [
    {
      route: RouteKey.LOVE,
      link: '/madivladi',
      writtenBy:
        'public.notify_appreciation() — supabase/migrations/022_fix_notification_links.sql',
    },
    {
      route: RouteKey.FAMILY_CALENDAR,
      link: '/family-calendar',
      writtenBy:
        'FAMILY_CALENDAR_LINK — supabase/functions/notify-event-reminder/index.ts',
    },
    {
      route: RouteKey.TODO_LIST,
      link: '/todo-list',
      writtenBy:
        'TODO_LIST_LINK — supabase/functions/notify-task-completed/index.ts',
    },
  ] as const;

/**
 * Paths that were once written into `notifications.link` and are not routes.
 *
 * Each maps to the route the writer meant. Migration 022 backfills exactly
 * these two, and the migration scan in `__tests__/notificationLinks.test.ts`
 * allows them to appear in checked-in SQL for that reason — a dead path NOT
 * listed here is a new bug, not a repair, and the test fails on it.
 *
 * Nothing reads this map at runtime: no row on the live project has ever
 * carried either value (`public.notifications` holds 5 rows, all with
 * `link IS NULL`), so there is no redirect to build. It is documentation with
 * a test attached.
 */
export const RETIRED_NOTIFICATION_LINKS: Readonly<Record<string, string>> = {
  '/love': ROUTE_PATHS[RouteKey.LOVE],
  '/calendar': ROUTE_PATHS[RouteKey.FAMILY_CALENDAR],
} as const;

// ============================================
// Route Check Helpers
// ============================================

/**
 * Check if a path matches a specific route
 */
export function isRoute(path: string, route: RouteKey): boolean {
  const routePath = ROUTE_PATHS[route];
  return path === routePath || path.startsWith(`${routePath}?`);
}

/**
 * Check if a path is a public route
 */
export function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTES.some(
    (route) =>
      path === ROUTE_PATHS[route] || path.startsWith(`${ROUTE_PATHS[route]}?`)
  );
}

/**
 * Check if a path is a protected route
 */
export function isProtectedRoute(path: string): boolean {
  return PROTECTED_ROUTES.some((route) => path.startsWith(ROUTE_PATHS[route]));
}

/**
 * Get route label for display
 */
export function getRouteLabel(route: RouteKey): string {
  const navItem = MAIN_NAV_ROUTES.find((item) => item.route === route);
  return navItem?.label ?? ROUTE_PATHS[route];
}
